/* The masthead: wordmark, station links, the station clock, and radio account.
 *
 * Sticky, because the one thing a listener does most is check what is on and
 * then go somewhere else, and a nav that has scrolled away costs a scroll back.
 *
 * THE CLOCK IS MAINLAND SPAIN TIME, ALWAYS, and it says so. It is not the visitor's
 * clock — they have one of those in the corner of their screen already. It is
 * there so that "20:00" anywhere else on the site has a reference point on the
 * same page, which is the fix for the timezone gap every station site has.
 * It re-renders on the minute boundary, not on a 1s interval: this is a
 * component that sits on screen for hours.
 *
 * The account is the one visually distinct thing up here. Exactly one primary
 * action per surface, or none of them is primary.
 *
 * Under 760px the links become a REAL disclosure: a <button aria-expanded>
 * controlling a panel, closing on Escape and on navigation, returning focus to
 * the button it came from. Not the checkbox hack — that gives a control with no
 * name, no state and no keyboard story, and it is invisible to every assistive
 * technology on the market.
 */

import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { formatTime, tzLabel } from '../format';
import { useAccount } from '../account';
import { useNarrow } from './shared';

/**
 * Below this the links row squeezes. Six links do not fit at 1024px otherwise,
 * and this row does not wrap — `.nav` and `.navlinks` are single flex lines, and
 * a single word cannot shrink below its own width, so the overflow would come
 * out as a horizontally scrolling page rather than a second row.
 *
 * The arithmetic, at 1024: the wordmark is ~190px of Anton, the station clock
 * ~105px, Support ~108px, and `.nav`'s own clamp(12px, 2.4vw, 36px) puts ~74px
 * of gap between those three and the links. That leaves ~490px, and six labels
 * at 13px with .06em tracking plus five clamp()ed gaps still need the tighter cut.
 *
 * `.nav .navlinks` already squeezes its gap on a clamp() for exactly this
 * reason; this squeezes the type on the same interval instead of adding an
 * eighth thing to a row that had run out of room. Above 1180 nothing changes.
 */
const TIGHT_NAV = 1180;

/** The clamp's own floor, so this is a width the design already sanctions. */
const TIGHT_ROW: CSSProperties = { gap: '10px' };
const TIGHT_LINK: CSSProperties = { fontSize: '0.75rem', letterSpacing: '.035em' };

/**
 * A handle is 3–24 characters and the chip it has to sit in is 104–216px wide,
 * so the two do not always agree. Archivo carries a WIDTH axis, so the fix is
 * to condense a long name rather than shrink it: the row keeps one type size
 * and `@aninconvenientlylonghandle` still reads as a name instead of as
 * `@anincon…`.
 *
 * Steps and not a measurement. Measuring means a layout read on every render
 * of a component that sits on screen for hours, and `text-overflow` is still
 * underneath for the handle that defeats even the narrowest cut — so the steps
 * only have to be close, and the ellipsis is honest about the rest.
 */
type Fit = 'wide' | 'mid' | 'narrow' | 'tight';

/** `budget` is what the box holds at full width; each step down the axis buys
    roughly 13%, 31% and 51% more characters at the same 13px. */
function fit(label: string, budget: number): Fit {
  if (label.length <= budget) return 'wide';
  if (label.length <= budget * 1.13) return 'mid';
  if (label.length <= budget * 1.31) return 'narrow';
  return 'tight';
}

/**
 * How many characters the chip holds at full width, per zone of the row —
 * each figure taken from the NARROWEST the chip's clamp() lets it be in that
 * zone, so a name that is set at a step is whole across the whole zone rather
 * than only at its wide end.
 *
 * 216px of chip past 1300, 138px at 1181, 104px at the floor; less 32px of
 * inset and border, over 9.9px per character of Archivo at 13px/800.
 */
const CHIP_ROOMY = 18;
const CHIP_CRAMPED = 10;
const CHIP_FLOOR = 7;
/** Where the chip's cap has climbed past anything a 24-character handle wants. */
const ROOMY_NAV = 1300;

/** The phone panel's button, measured against a 320px screen — the narrowest
    phone still in circulation, and the only one this label does not fit on. */
const PANEL_BUDGET = 25;

interface NavItem {
  to: string;
  label: string;
  /** Which paths light this link up. `/episodes/HGR-047` is still Archive. */
  match: (pathname: string) => string[];
}

const LINKS: NavItem[] = [
  { to: '/', label: 'Live', match: () => ['/'] },
  { to: '/schedule', label: 'Schedule', match: () => ['/schedule'] },
  /* Before Archive, not after it. The archive is where the station has been;
     premieres are the thing it is for — a label radio debuting records before
     they come out — and it is the section an artist arrives looking for. */
  { to: '/premieres', label: 'Premieres', match: () => ['/premieres'] },
  { to: '/archive', label: 'Archive', match: () => ['/archive', '/genre', '/episodes'] },
  // The full collective name heads the roster page; the compact form keeps the
  // desktop navigation from wrapping before its mobile breakpoint.
  { to: '/residents', label: 'Rhythm Police', match: () => ['/residents'] },
  { to: '/submit', label: 'Submit', match: () => ['/submit'] },
];

function isCurrent(item: NavItem, pathname: string): boolean {
  const prefixes = item.match(pathname);
  if (item.to === '/') return pathname === '/';
  return prefixes.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

/**
 * `21:12 CEST`.
 *
 * The abbreviation is taken off format.ts's own label rather than asked of
 * Intl again, so the nav and the schedule can never disagree about whether it
 * is CET or CEST — there is one formatter and one cache behind both.
 */
function stationClock(at: Date): string {
  const label = tzLabel('station', at);
  const parts = label.split('·');
  const abbreviation = parts.length > 1 ? parts[parts.length - 1].trim() : '';
  return `${formatTime(at, 'station')}${abbreviation ? ` ${abbreviation}` : ''}`;
}

export function Nav() {
  const { account, loading } = useAccount();
  const { pathname } = useLocation();
  const narrow = useNarrow();
  const tight = useNarrow(TIGHT_NAV);
  const roomy = !useNarrow(ROOMY_NAV);
  const [open, setOpen] = useState(false);
  const [now, setNow] = useState(() => new Date());
  const burgerRef = useRef<HTMLButtonElement | null>(null);

  /* Tick on the minute boundary. A 1000ms interval would re-render this
     component 3,600 times an hour to change nothing 59 times out of 60. */
  useEffect(() => {
    let timer = 0;
    const schedule = () => {
      const untilNextMinute = 60_000 - (Date.now() % 60_000);
      timer = window.setTimeout(() => {
        setNow(new Date());
        schedule();
      }, untilNextMinute + 50);
    };
    schedule();
    return () => window.clearTimeout(timer);
  }, []);

  /* Navigating closes the panel. Without this, tapping a link on a phone
     leaves the menu open over the page it just went to. */
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  /* The panel only exists under 760px. If the window is widened while it is
     open, close it rather than leave a hidden thing in the tab order. */
  useEffect(() => {
    if (!narrow) setOpen(false);
  }, [narrow]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      burgerRef.current?.focus();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  const clock = stationClock(now);
  const accountLabel = loading ? 'ACCOUNT' : account ? `@${account.username}` : 'LOGIN';
  const panelLabel = account ? `PROFILE · @${account.username}` : 'LOGIN / CREATE ACCOUNT';
  const chipBudget = tight ? CHIP_FLOOR : roomy ? CHIP_ROOMY : CHIP_CRAMPED;

  return (
    <nav className="nav" aria-label="Station">
      <Link className="wordmark" to="/">
        Higher Ground Radio
      </Link>

      <div className="navlinks" style={tight ? TIGHT_ROW : undefined}>
        {LINKS.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            aria-current={isCurrent(item, pathname) ? 'page' : undefined}
            style={tight ? TIGHT_LINK : undefined}
          >
            {item.label}
          </Link>
        ))}
      </div>

      {/* A <time> without a datetime is still the right element, and the
          visually hidden half says which clock this is. */}
      <div className="mono dim nav__clock">
        <span className="vh">Station time, </span>
        {clock}
      </div>

      {/* The name is a child and not the button's own text: `text-overflow`
          ellipsises a block container's line box, and .btn is a flex one — it
          would clip a long handle mid-letter with no ellipsis at all. */}
      <Link
        className="btn nav__account"
        to="/account"
        aria-current={pathname === '/account' ? 'page' : undefined}
        title={account ? `Open @${account.username}'s radio profile` : 'Log in to Higher Ground'}
      >
        <span className="nav__fit" data-fit={fit(accountLabel, chipBudget)}>
          {accountLabel}
        </span>
      </Link>

      <button
        type="button"
        className="nav__burger"
        ref={burgerRef}
        aria-expanded={open}
        aria-controls="nav-panel"
        onClick={() => setOpen((was) => !was)}
      >
        <span className="vh">{open ? 'Close the menu' : 'Menu'}</span>
        <span className="nav__burgerbars" aria-hidden="true">
          <i />
          <i />
          <i />
        </span>
      </button>

      <div className="nav__panel" id="nav-panel" hidden={!open}>
        <div className="mono dim nav__panelclock">{clock}</div>
        <ul className="nav__panellist">
          {LINKS.map((item) => (
            <li key={item.to}>
              <Link
                to={item.to}
                aria-current={isCurrent(item, pathname) ? 'page' : undefined}
              >
                {item.label}
              </Link>
            </li>
          ))}
        </ul>
        <Link className="btn nav__panelaccount" to="/account">
          <span className="nav__fit" data-fit={fit(panelLabel, PANEL_BUDGET)}>
            {panelLabel}
          </span>
        </Link>
      </div>
    </nav>
  );
}
